/**
 * Which news events get chart real estate.
 *
 * The drawer lists everything; the chart must not. Two filters apply:
 *
 *  1. **High impact only.** A vertical line per Low-impact print would bury
 *     the candles — the overlay exists for the releases that actually move
 *     price.
 *  2. **Relevant to the charted symbol.** A JPY release is noise on EURUSD.
 *     Global ("ALL") events — OPEC, G20 — always pass, since they move
 *     everything.
 *
 * Restricted events are not filtered here. The caller (Chart.tsx) narrows the
 * FTMO feed to restricted releases before this runs, so a restricted news
 * item gets exactly one line; without FTMO data every high-impact release
 * passes and is drawn in the non-restricted colour.
 */
import type { NewsEvent } from "@/api/types";

/** Currencies that actually appear in the calendar feeds. Used to tell a
 *  real currency leg (USD) from a metal / crypto prefix (XAU, BTC). */
const CALENDAR_CURRENCIES = new Set([
  "USD", "EUR", "GBP", "JPY", "CHF", "CAD", "AUD", "NZD", "CNY",
]);

/**
 * Currency legs a symbol is exposed to.
 *
 * `EURUSD` → EUR + USD. `XAUUSD` → USD (XAU is not a calendar currency).
 * Anything we can't split — `USOIL`, `USTEC`, `BTCUSD` — falls back to USD,
 * which is correct for every instrument in `symbols.json`: they're all
 * USD-quoted. If a non-USD-quoted instrument is ever added, extend this.
 */
export function currenciesForSymbol(symbol: string | null | undefined): string[] {
  const s = (symbol ?? "").toUpperCase();
  const legs = new Set<string>();
  if (s.length === 6) {
    for (const leg of [s.slice(0, 3), s.slice(3)]) {
      if (CALENDAR_CURRENCIES.has(leg)) legs.add(leg);
    }
  }
  if (legs.size === 0) legs.add("USD");
  return [...legs];
}

/** The subset of `events` that should render on a chart of `symbol`. */
export function selectChartNewsEvents(
  events: NewsEvent[],
  symbol: string | null | undefined,
): NewsEvent[] {
  const legs = new Set(currenciesForSymbol(symbol));
  return events.filter(
    (e) =>
      e.impact === "high" && (legs.has(e.currency) || e.currency === "ALL"),
  );
}

/** Events collapsed into one marker per timestamp — simultaneous releases
 *  (NFP + Unemployment Rate + Average Hourly Earnings all print at 12:30)
 *  would otherwise stack three identical lines on the same pixel. */
export interface NewsMarker {
  /** Unix SECONDS — the chart's time unit. */
  time: number;
  events: NewsEvent[];
  /** True when ANY event in the group is FTMO-restricted. */
  restricted: boolean;
}

export function groupNewsMarkers(events: NewsEvent[]): NewsMarker[] {
  const byTime = new Map<number, NewsEvent[]>();
  for (const e of events) {
    const ms = Date.parse(e.time_utc);
    if (Number.isNaN(ms)) continue;
    const t = Math.floor(ms / 1000);
    const bucket = byTime.get(t);
    if (bucket) bucket.push(e);
    else byTime.set(t, [e]);
  }
  return [...byTime.entries()]
    .map(([time, evs]) => ({
      time,
      events: evs,
      restricted: evs.some((e) => e.restricted),
    }))
    .sort((a, b) => a.time - b.time);
}
